'use client';

import { useCallback, useRef } from 'react';
import {
  applyBgPositionPreset,
  BG_POSITION_PRESETS,
  BG_SCALE_MAX,
  BG_SCALE_MIN,
  getBgFocal,
  patchBgFocal,
  patchBgScale,
  slideBgImageStyle,
} from '@/lib/deck/bg-helpers';
import type { ObjectPosition, SlideBg } from '@/lib/deck/types';

const FIELD = 'font-dank-mono text-[10px] uppercase tracking-widest text-slate-400';
const BTN =
  'rounded-full border border-beige px-2.5 py-0.5 font-inter text-[11px] text-slate-600 hover:border-marine-green hover:text-marine-green';

interface BackgroundAdjustControlsProps {
  bg: SlideBg;
  onChange: (bg: SlideBg) => void;
}

export function BackgroundAdjustControls({ bg, onChange }: BackgroundAdjustControlsProps) {
  const focal = getBgFocal(bg);
  const scale = bg.scale ?? 1;
  const previewRef = useRef<HTMLDivElement>(null);

  const setFocal = useCallback(
    (x: number, y: number) => {
      onChange(patchBgFocal(bg, x, y));
    },
    [bg, onChange],
  );

  const startPreviewDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = previewRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();

      const pick = (clientX: number, clientY: number) => {
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;
        setFocal(x, y);
      };

      pick(e.clientX, e.clientY);

      const onMove = (ev: PointerEvent) => pick(ev.clientX, ev.clientY);
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [setFocal],
  );

  if (!bg.src) return null;

  const fit = bg.objectFit === 'contain' ? 'object-contain' : 'object-cover';

  return (
    <div className="space-y-3 rounded-xl border border-beige bg-white p-4">
      <p className={FIELD}>Image position &amp; zoom</p>
      <p className="font-inter text-[11px] text-slate-500">
        Drag the preview to set the focal point. Pan on the canvas by dragging empty space.
      </p>

      <div
        ref={previewRef}
        className="relative aspect-[16/9] w-full cursor-crosshair overflow-hidden rounded-lg border border-beige bg-[#0D0D14]"
        onPointerDown={startPreviewDrag}
        role="presentation"
      >
        <img
          src={bg.src}
          alt=""
          className={`h-full w-full ${fit}`}
          style={slideBgImageStyle(bg)}
          draggable={false}
        />
        <div
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-marine-green shadow-md"
          style={{ left: `${focal.x}%`, top: `${focal.y}%` }}
          aria-hidden
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="font-inter text-[10px] text-slate-500">
          Horizontal · {Math.round(focal.x)}%
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={focal.x}
            onChange={(e) => setFocal(Number(e.target.value), focal.y)}
            className="mt-1 w-full accent-marine-green"
          />
        </label>
        <label className="font-inter text-[10px] text-slate-500">
          Vertical · {Math.round(focal.y)}%
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={focal.y}
            onChange={(e) => setFocal(focal.x, Number(e.target.value))}
            className="mt-1 w-full accent-marine-green"
          />
        </label>
      </div>

      <label className="block font-inter text-[10px] text-slate-500">
        Zoom · {Math.round(scale * 100)}%
        <input
          type="range"
          min={BG_SCALE_MIN}
          max={BG_SCALE_MAX}
          step={0.05}
          value={scale}
          onChange={(e) => onChange(patchBgScale(bg, Number(e.target.value)))}
          className="mt-1 w-full accent-marine-green"
        />
      </label>

      <div className="flex flex-wrap gap-1.5">
        {BG_POSITION_PRESETS.map((p) => {
          const active = (bg.position ?? 'center') === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(applyBgPositionPreset(bg, p.id as ObjectPosition))}
              className={`${BTN} ${active ? 'border-marine-green bg-marine-green/10 text-marine-green' : ''}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-2 font-inter text-xs text-slate-600">
        <input
          type="checkbox"
          checked={!!bg.flipX}
          onChange={(e) => onChange({ ...bg, flipX: e.target.checked || undefined })}
        />
        Flip image horizontally
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(patchBgScale(bg, scale - 0.05))}
          className={`${BTN} flex-1`}
        >
          Zoom −
        </button>
        <button
          type="button"
          onClick={() => onChange(patchBgScale(bg, scale + 0.05))}
          className={`${BTN} flex-1`}
        >
          Zoom +
        </button>
        <button
          type="button"
          onClick={() => {
            const reset = applyBgPositionPreset({ ...bg }, 'center');
            delete reset.scale;
            onChange(reset);
          }}
          className={`${BTN} flex-1`}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
